import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import {
  formatContactAddress,
  parseContactAddress,
} from "../../../../../../../../../../../_shared/_logic/contact-addresses"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import {
  sourceCleanupInstruction,
  type SourceCleanupInstructionPresentation,
} from "../../../../../_shared/_logic/source-cleanup-instruction-tags"
import {
  normalizeContactPointsOutput,
  type ContactPointMutationSnapshot,
  type NormalizedContactPointDecision,
} from "../../../_shared/_logic/contact-point-output"
import type { ContactPointSourceCleanupRow } from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) =>
  item.action_case.flow_key === workflow.flow.flow_key &&
  (item.steps.some((entry) => entry.step_key === step.step_key) ||
    step.requirements_resolved_by_step.some((requirement) =>
      item.action_case.requirements.includes(requirement),
    ))

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const payloadPresentation = (payload: JsonObject, item: InboxWorkflowItem) => {
  const property = objectValue(payload.property)
  const address = objectValue(property.address)
  const unit = objectValue(property.unit)
  const requester = objectValue(payload.requester)
  const customer = objectValue(requester.verified_customer)
  const fallbackCustomer = objectValue(requester.customer)
  const contact = objectValue(requester.contact)
  const presentation = objectValue(payload.presentation)
  const origin = objectValue(payload.origin)
  const customerId = stringValue(customer.id, fallbackCustomer.id, payload.customer_id) || null

  return {
    sourceLabel: formatInboxSourceLabel(
      stringValue(
        presentation.source_label,
        payload.source_label,
        origin.source,
        item.action_case.source,
        item.action_case.channel,
      ),
    ),
    sourceSubLabel: stringValue(
      presentation.source_subline,
      presentation.source_sub_label,
      payload.source_subline,
    ),
    addressLabel:
      stringValue(
        address.label,
        address.freeform,
        property.label,
        payload.address_label,
        payload.property_label,
        payload.property_address,
        payload.address,
      ) || "Liegenschaft",
    unitLabel: formatPropertyUnitLabel(
      stringValue(
        unit.label,
        unit.hint,
        payload.property_unit_label,
        payload.unit_label,
        payload.unit_hint,
      ) || "Gebäudeeinheit",
    ),
    customerLabel:
      stringValue(
        customer.label,
        customer.name,
        fallbackCustomer.label,
        fallbackCustomer.name,
        requester.submitted_name,
        payload.customer_label,
        payload.customer_name,
      ) || "Ansprechpartner",
    customerRoleLabel:
      stringValue(
        customer.role,
        fallbackCustomer.role,
        customer.role_hint,
        fallbackCustomer.role_hint,
        requester.submitted_role,
        requester.suggested_role,
        payload.customer_role_hint,
        payload.property_customer_role,
      ) || (customerId ? "Ansprechpartner" : ""),
    customerEmail: stringValue(contact.email, payload.contact_email, payload.email),
    customerPhone: stringValue(contact.phone, payload.contact_phone, payload.phone),
  }
}

const kindLabel = (value: unknown) => {
  const kind = stringValue(value).toUpperCase()

  if (kind === "EMAIL") return "E-Mail"

  if (kind === "MOBILE") return "Mobil"

  if (kind === "FIXED_LINE") return "Festnetz"

  if (kind === "ADDRESS") return "Adresse"

  return "Kontaktweg"
}

const contactPointValue = (contactPoint: ContactPointMutationSnapshot) => {
  if (contactPoint.kind !== "ADDRESS") return contactPoint.value

  const address = parseContactAddress(contactPoint.value)
  const hasAddressParts = [
    address.street,
    address.house_number,
    address.zip,
    address.city,
    address.address_extra,
  ].some(Boolean)

  return hasAddressParts ? formatContactAddress(address) : contactPoint.value
}

const sourceInstruction = (
  decision: NormalizedContactPointDecision,
): SourceCleanupInstructionPresentation | null => {
  const affected = decision.affectedContactPoint

  if (!affected) return null

  const kind = kindLabel(affected.kind)
  const affectedValue = contactPointValue(affected)

  if (decision.action === "replace" && decision.replacementContactPoint) {
    const replacementValue = contactPointValue(decision.replacementContactPoint)

    return sourceCleanupInstruction(
      `${affectedValue} → ${replacementValue}`,
      `${kind} ersetzen`,
      replacementValue,
    )
  }

  if (decision.action === "delete") {
    return sourceCleanupInstruction(affectedValue, `${kind} löschen`, affectedValue)
  }

  if (decision.action === "reject") {
    return sourceCleanupInstruction(affectedValue, `${kind} nicht übernehmen`, affectedValue)
  }

  return sourceCleanupInstruction(affectedValue, `${kind} ergänzen`, affectedValue)
}

const priorOutputError = "Gespeicherte Kontaktpunkt-Entscheidung fehlt oder ist ungültig."
const cleanupOutputError = "Gespeicherter Abschluss des Quellenabgleichs fehlt oder ist ungültig."

export const buildContactPointSourceCleanupRows = ({
  workflow,
  step,
  rowOutcomes,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
}): ContactPointSourceCleanupRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const contactPointsOutput = stepOutput(item, "review_contact_points")
      const cleanupOutput = stepOutput(item, "source_cleanup")
      const presentation = payloadPresentation(payload, item)
      const normalizedOutput = normalizeContactPointsOutput(contactPointsOutput)
      const projectedInstructions = normalizedOutput?.decisions.map(sourceInstruction) ?? null
      const instructions = projectedInstructions?.every(
        (instruction): instruction is SourceCleanupInstructionPresentation => instruction !== null,
      )
        ? projectedInstructions
        : null
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const outputError = !instructions
        ? priorOutputError
        : resolved && cleanupOutput.confirmed !== true
          ? cleanupOutputError
          : null
      const completed = cleanupOutput.confirmed === true

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        ...presentation,
        sourceSyncInstructions: instructions ?? [],
        completed,
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled:
          outcome?.status === "success" || !needsStep || !item.compatible || Boolean(outputError),
        statusLabel:
          outcome?.status === "failed"
            ? "Prüfung nötig"
            : outputError
              ? "Datenfehler"
              : skipped
                ? "Übersprungen"
                : completed
                  ? "Erledigt"
                  : item.compatible
                    ? "Offen"
                    : "Nicht verfügbar",
        statusDetail: outcome?.error || outcome?.detail || outputError || item.error || null,
        searchText: [
          presentation.sourceLabel,
          presentation.addressLabel,
          presentation.unitLabel,
          presentation.customerLabel,
          presentation.customerRoleLabel,
          ...(instructions ?? []).flatMap((instruction) => [
            instruction.label,
            instruction.subLabel,
            instruction.copyValue,
          ]),
        ].join(" "),
      }
    })
